import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/audit/orgs/index.tsx
 * @description 組織資訊列表頁（M1）。
 *
 * 功能：
 * - 列出所有客戶組織（auditor 只見自己案件關聯的；manager/admin 見全部）
 * - 搜尋（名稱/驗證註冊碼）
 * - 點擊行 → 組織詳情頁 /audit/orgs/:clientId
 *
 * 視覺：ListPageShell + FilterBar + StatusPill
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Input, Select, Table } from 'antd';
import { Building2, RefreshCw, Search } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  ListPageShell,
  FilterBar,
  FilterBarLabel,
  StatusPill,
  type StatusPillProps,
} from '@/components/shell';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/audit/orgs/')({ component: OrgListPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface Client {
  id: number;
  clientCode: string;
  name: string;
  scheme: string;
  qualification: string;
  verificationStatus: string;
  paymentStatus: string;
  contractExpiresAt: string | null;
}

interface ListResponse {
  items: Client[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── 驗證狀態顏色 ─────────────────────────────────────────────────────────────

const VS_TONE: Record<string, StatusPillProps['tone']> = {
  新申請: 'info',
  維持: 'success',
  結束驗證: 'muted',
  撤銷驗證: 'danger',
  暫時終止驗證_TOP: 'warning',
  暫時停止驗證_TAP: 'warning',
  恢復驗證: 'brand',
  終止驗證: 'danger',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 組織列表頁。
 */
function OrgListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const isAuditor = user.roles.includes('auditor');

  const [search, setSearch] = useState('');
  const [vsFilter, setVsFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, refetch } = useQuery<ListResponse>({
    queryKey: ['audit-orgs', page, search, vsFilter],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (search) params.set('search', search);
      if (vsFilter) params.set('verificationStatus', vsFilter);
      return apiClient.get(`/api/settings/clients?${params.toString()}`);
    },
  });

  const listData = data;

  const columns: ColumnsType<Client> = [
    {
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 140,
      render: (code: string, record) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: 'monospace', padding: 0 }}
          onClick={() =>
            void navigate({ to: '/audit/orgs/$clientId', params: { clientId: String(record.id) } })
          }
        >
          {code}
        </Button>
      ),
    },
    {
      title: '名稱',
      dataIndex: 'name',
      ellipsis: true,
      render: (name: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Building2 size={14} style={{ color: '#94a3b8', flexShrink: 0 }} />
          {name}
        </span>
      ),
    },
    { title: '方案', dataIndex: 'scheme', width: 70 },
    { title: '資格', dataIndex: 'qualification', width: 120, ellipsis: true },
    {
      title: '驗證狀態',
      dataIndex: 'verificationStatus',
      width: 140,
      render: (s: string) => (
        <StatusPill tone={VS_TONE[s] ?? 'neutral'} dot size="sm">
          {s}
        </StatusPill>
      ),
    },
    {
      title: '合約到期',
      dataIndex: 'contractExpiresAt',
      width: 120,
      render: (d: string | null) => {
        if (!d) return '—';
        const dt = new Date(d);
        const isExpiring = dt.getTime() - Date.now() < 90 * 86400000;
        return (
          <span style={{ color: isExpiring ? '#e11d48' : undefined }}>
            {formatDate(dt)}
            {isExpiring && ' ⚠️'}
          </span>
        );
      },
    },
  ];

  return (
    <ListPageShell
      title={`組織資訊${isAuditor ? '（我的案件）' : ''}`}
      description="管理受驗證的客戶組織基本資料與驗證狀態"
      filters={
        <FilterBar
          search={
            <Input
              prefix={<Search size={14} />}
              placeholder="搜尋組織名稱或驗證註冊碼…"
              value={search}
              allowClear
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          }
          filters={
            <>
              <FilterBarLabel>驗證狀態</FilterBarLabel>
              <Select
                allowClear
                placeholder="全部狀態"
                value={vsFilter || undefined}
                onChange={(v) => {
                  setVsFilter(v ?? '');
                  setPage(1);
                }}
                style={{ width: 160 }}
                options={[
                  { value: '新申請', label: '新申請' },
                  { value: '維持', label: '維持' },
                  { value: '結束驗證', label: '結束驗證' },
                  { value: '撤銷驗證', label: '撤銷驗證' },
                  { value: '終止驗證', label: '終止驗證' },
                ]}
              />
            </>
          }
          extra={
            <Button icon={<RefreshCw size={14} />} onClick={() => void refetch()}>
              刷新
            </Button>
          }
        />
      }
    >
      <Table<Client>
        dataSource={listData?.items ?? []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: listData?.total ?? 0,
          onChange: setPage,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        onRow={(record) => ({
          onClick: () =>
            void navigate({ to: '/audit/orgs/$clientId', params: { clientId: String(record.id) } }),
          style: { cursor: 'pointer' },
        })}
      />
    </ListPageShell>
  );
}
